import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Icon from '../../components/dashboard/DashboardIcons';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../../lib/whatsapp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SUB_LABELS = { active: 'نشط', expired: 'منتهي', suspended: 'موقوف', cancelled: 'ملغى' };
const POSITIONS = { goalkeeper: 'حارس مرمى', defender: 'مدافع', midfielder: 'وسط', forward: 'مهاجم' };
const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');

const Section = ({ title, icon, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`parent-child-section-${title}`}>
    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><Icon name={icon} className="h-4 w-4 text-teal-500" /> {title}</div>
    {children}
  </div>
);

export default function ParentChildDetailPage() {
  const { playerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/parent/me/child/${playerId}`, auth())
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.detail || 'تعذر تحميل بيانات الابن'));
  }, [playerId]);

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="parent-child-detail-error">{error}</div>
    );
  }
  if (!data) {
    return <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>;
  }

  const waNumber = data.whatsapp_enabled ? normalizeWhatsAppNumber(data.whatsapp_number) : null;
  const openWhatsApp = () => {
    const parentName = getUser().full_name || '';
    const clubLabel = data.whatsapp_display_name || data.club_name || 'الأكاديمية';
    const text = `مرحباً، معي ولي الأمر ${parentName}، أرغب بالتواصل بخصوص ${data.player_name} في ${clubLabel}.`;
    window.open(buildWaMeLink(waNumber, text), '_blank');
  };

  return (
    <div className="space-y-5" data-testid="parent-child-detail-page">
      <Link to="/parent/children" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-teal-600">
        <Icon name="chevronLeft" className="h-3.5 w-3.5 rotate-180" /> رجوع لأبنائي
      </Link>

      <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
        {data.photo_url ? (
          <img src={data.photo_url} alt={data.player_name} className="h-20 w-20 rounded-2xl object-cover" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-teal-50 text-2xl font-bold text-teal-600">{data.player_name?.charAt(0)}</div>
        )}
        <div>
          <div className="text-lg font-black text-[#0A192F]" data-testid="parent-child-detail-name">{data.player_name}</div>
          <div className="text-sm text-slate-500">{data.club_name} {data.category_name ? `· ${data.category_name}` : ''}</div>
          <div className="text-xs text-slate-400">{POSITIONS[data.position] || ''} {data.jersey_number ? `· رقم ${data.jersey_number}` : ''}</div>
        </div>
      </div>

      {waNumber && (
        <button
          onClick={openWhatsApp}
          data-testid="parent-child-whatsapp-contact-btn"
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm font-bold text-teal-700 hover:bg-teal-100"
        >
          <span className="flex items-center gap-2"><Icon name="whatsapp" className="h-4 w-4" /> تواصل مع الأكاديمية عبر واتساب بخصوص {data.player_name}</span>
          <Icon name="chevronLeft" className="h-4 w-4" />
        </button>
      )}

      <Section title="الاشتراك" icon="wallet">
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><span className="text-xs text-slate-400">الحالة: </span><span className="font-bold text-slate-700">{SUB_LABELS[data.subscription.status] || 'غير محدد'}</span></div>
          <div><span className="text-xs text-slate-400">الباقة: </span><span className="font-bold text-slate-700">{data.subscription.package || '—'}</span></div>
          <div><span className="text-xs text-slate-400">البداية: </span><span className="font-bold text-slate-700">{data.subscription.start || '—'}</span></div>
          <div><span className="text-xs text-slate-400">النهاية: </span><span className="font-bold text-slate-700">{data.subscription.end || '—'}</span></div>
        </div>
      </Section>

      <Section title="الحضور والانصراف (آخر 30 سجل)" icon="clock">
        <p className="mb-2 -mt-1 text-[11px] text-slate-400">النظام يسجّل حالياً وقت الحضور (تسجيل الدخول) فقط عبر مسح الباركود، ولا يوجد تسجيل انصراف منفصل بعد.</p>
        {data.attendance.length === 0 ? (
          <p className="text-xs text-slate-400">لا توجد سجلات حضور بعد</p>
        ) : (
          <div className="space-y-1.5" data-testid="parent-child-attendance-list">
            {data.attendance.slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
                <span className="text-slate-600">{a.date} {a.time ? `· ${a.time}` : ''}</span>
                <span className="font-bold text-teal-700">حضور (دخول)</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="الجداول والمباريات القادمة" icon="calendar">
        {data.upcoming_sessions.length === 0 && data.upcoming_matches.length === 0 ? (
          <p className="text-xs text-slate-400">لا توجد تدريبات أو مباريات مجدولة قريباً</p>
        ) : (
          <div className="space-y-1.5" data-testid="parent-child-schedule-list">
            {data.upcoming_sessions.map((s, i) => (
              <div key={`s-${i}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
                <span className="text-slate-600">تدريب: {s.title} — {s.date} {s.start_time || ''}</span>
              </div>
            ))}
            {data.upcoming_matches.map((m, i) => (
              <div key={`m-${i}`} className="flex items-center justify-between rounded-lg bg-orange-50 px-3 py-2 text-xs">
                <span className="text-slate-600">مباراة — {new Date(m.match_date).toLocaleDateString('ar-SA')}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="التقييمات" icon="star">
        <p className="text-xs text-slate-400">لا تتوفر تقييمات لهذا اللاعب حالياً</p>
      </Section>
    </div>
  );
}
